import React from 'react'
import { BiPencil } from 'react-icons/bi'
import {
  AccountCustomServiceFee,
  GroupOrderCustomServiceFee,
  GroupOrderCustomServiceFeeSettings,
} from '@types'

const getCustomServiceFeeDescription = (
  config: GroupOrderCustomServiceFee[] | null,
) => {
  if (!config) {
    return 'N/A'
  }

  return config.map((customServiceFee, idx) => {
    const {
      serviceFeeType,
      feeDescription,
      valueCalculationType,
      value,
      percentValue,
      isTaxExempt,
      itemTaxCode,
    } = customServiceFee
    let valueDisplay = 'N/A'
    if (valueCalculationType === 'Flat Price' && value) {
      valueDisplay = `$${value}`
    } else if (valueCalculationType === 'Percent of Subtotal' && percentValue) {
      valueDisplay = `${percentValue}% of subtotal`
    }
    const taxDisplay = !isTaxExempt ? `, Taxable: ${itemTaxCode}` : ''

    return (
      <div
        key={`custom-service-fee-${idx}`}
        className="custom-service-fee-item"
      >
        <div className="fee-label">
          <p className="fee-type">{serviceFeeType}</p>
          <p className="fee-description">{feeDescription}</p>
        </div>
        <div className="fee-value">
          {valueDisplay}
          {taxDisplay}
        </div>
      </div>
    )
  })
}

const getDescription = (
  client: {
    accountCustomServiceFees: AccountCustomServiceFee[]
  },
  clientGlobalSettings: {
    customServiceFeeSettings: GroupOrderCustomServiceFeeSettings
  },
  clientSettingsCollection: {
    name: string
    customServiceFeeSettings: GroupOrderCustomServiceFeeSettings
  }[],
) => {
  const globalConfig =
    clientGlobalSettings && clientGlobalSettings.customServiceFeeSettings
      ? clientGlobalSettings.customServiceFeeSettings
      : null
  const hasGlobalConfig = Boolean(globalConfig)
  const hasLocationSpecificConfig = clientSettingsCollection.some((cs: any) =>
    Boolean(cs.customServiceFeeSettings),
  )
  if (hasGlobalConfig) {
    return (
      <div>
        <div className="description-label">Set globally to:</div>
        <div className="config-description global-description">
          {getCustomServiceFeeDescription(globalConfig?.serviceFees || null)}
        </div>
      </div>
    )
  } else if (hasLocationSpecificConfig) {
    return (
      <div>
        <div className="description-label">Set at location level:</div>
        {clientSettingsCollection.map((cs: any) => {
          return (
            <div key={cs.id} className="location-item">
              <div className="location-name">{cs.name}</div>
              <div className="config-description">
                {getCustomServiceFeeDescription(
                  cs.customServiceFeeSettings?.serviceFees || null,
                )}
              </div>
            </div>
          )
        })}
      </div>
    )
  } else {
    if (client.accountCustomServiceFees.length > 0) {
      return (
        <div>
          <div className="description-label">Using account defaults:</div>
          <div className="config-description">
            {getCustomServiceFeeDescription(client.accountCustomServiceFees)}
          </div>
        </div>
      )
    } else {
      return (
        <div>
          <div className="description-label">N/A</div>
        </div>
      )
    }
  }
}

interface CustomServiceFeeSettingsSummaryProps {
  client: {
    accountCustomServiceFees: AccountCustomServiceFee[]
  }
  clientGlobalSettings: {
    customServiceFeeSettings: GroupOrderCustomServiceFeeSettings
  }
  clientSettingsCollection: {
    name: string
    customServiceFeeSettings: GroupOrderCustomServiceFeeSettings
  }[]
  onRequestEdit: () => void
}

const CustomServiceFeeSettingsSummary: React.FC<
  CustomServiceFeeSettingsSummaryProps
> = ({
  client,
  clientGlobalSettings,
  clientSettingsCollection,
  onRequestEdit,
}) => {
  const description = getDescription(
    client,
    clientGlobalSettings,
    clientSettingsCollection,
  )

  return (
    <div className="custom-service-fee-settings-summary">
      <div className="summary-header">
        <h3>Client Global Custom Service Fee Settings</h3>
        <div>
          <button onClick={onRequestEdit} className="edit-button">
            <div className="button-content">
              <BiPencil />
              <div>Edit</div>
            </div>
          </button>
        </div>
      </div>
      <div className="summary-description">{description}</div>
    </div>
  )
}

export default CustomServiceFeeSettingsSummary
